import React, { useEffect, useRef, useState } from 'react';
import { tidyAddress } from '../../utils/address';
import { useNavigate } from 'react-router-dom';
import { fmtDate as fmtDateLocal } from '../../utils/ui';
import { PRICE_STRUCTURES, WORK_TYPES, workTypeLabel } from '../../utils/bidTypes';
import AdminLayout from './AdminLayout';
import ActivityPills from '../../components/ActivityPills';
import BidInboxWindow from './BidInbox';
import OldJobWizard from './OldJobWizard';
import { ContactManager, CompanyCombo, buildCompanies, PreBidBlock } from './AdminBidDetail';
import RateSheetModal from '../../components/RateSheetModal';
import { ContactCheck } from '../../components/ContactCheck';
import { findPlace } from '../../utils/placeSearch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const STATUS_META = {
  potential: { label: 'Potential', color: 'purple' },
  draft:     { label: 'Upcoming',  color: 'gray' },
  submitted: { label: 'Submitted', color: 'blue' },
  told_low:  { label: 'Told Low',  color: 'green' },
  awarded:   { label: 'Awarded',   color: 'orange' },
  lost:      { label: 'Lost',      color: 'gray' },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).length <= 10 ? dateStr + 'T12:00:00' : dateStr);
  if (isNaN(d)) return null;
  const today = new Date(); today.setHours(12, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}
// Only un-submitted bids get flagged: red if the bid date has passed, yellow if
// it's within 3 days. Submitted/awarded/lost are done — no color.
function bidUrgency(bid) {
  if (!bid || ['potential', 'submitted', 'told_low', 'awarded', 'lost'].includes(bid.status)) return null;
  const days = daysUntil(bid.bid_date);
  if (days == null) return null;
  if (days < 0) return 'red';
  if (days <= 3) return 'yellow';
  return null;
}
const URGENCY = {
  red:    { bg: '#fee2e2', color: '#b91c1c', border: '#dc2626' },
  yellow: { bg: '#fef3c7', color: '#b45309', border: '#f59e0b' },
};
function fmtPreBid(dt) { if (!dt) return ''; const d = new Date(dt); return isNaN(d) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }

function LocationPickerModal({ value, onConfirm, onClose }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);
  const [search, setSearch] = useState(value || '');
  const [chosen, setChosen] = useState(value || '');
  const [searching, setSearching] = useState(false);
  const [miss, setMiss] = useState('');   // the search found nothing, or found a simpler version (Oct 8 2026)

  useEffect(() => {
    // Dynamically load Leaflet CSS + JS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    function initMap(L) {
      if (!mapRef.current || leafletRef.current) return;
      const map = L.map(mapRef.current).setView([39.5296, -119.8138], 12); // default: Reno
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      const icon = L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41], iconAnchor: [12, 41],
      });

      leafletRef.current = map;

      map.on('click', async e => {
        const { lat, lng } = e.latlng;
        if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
        else { markerRef.current = L.marker([lat, lng], { icon }).addTo(map); }
        // Reverse geocode
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const d = await r.json();
          const addr = tidyAddress(d.display_name) || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          setChosen(addr);
          setSearch(addr);
        } catch {
          setChosen(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        }
      });

      // If there's an existing value, geocode it to center the map
      if (value) {
        findPlace(value).then(h => (h ? [{ lat: h.lat, lon: h.lng }] : []))
          .then(results => {
            if (results[0]) {
              const lat = parseFloat(results[0].lat), lng = parseFloat(results[0].lon);
              map.setView([lat, lng], 15);
              markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
            }
          }).catch(() => {});
      }
    }

    if (window.L) { initMap(window.L); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap(window.L);
    document.head.appendChild(script);

    return () => { if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; markerRef.current = null; } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function doSearch() {
    if (!search.trim() || !leafletRef.current) return;
    setSearching(true);
    try {
      // Tries the address as typed, then simpler versions (no building / suite / brackets) — see utils/placeSearch.
      const hit = await findPlace(search);
      const results = hit ? [{ lat: hit.lat, lon: hit.lng, display_name: hit.exact ? hit.display : search.trim() }] : [];
      setMiss(!hit ? 'Couldn’t find that on the map — try just the street and town, or click the map to drop the pin.' : hit.exact ? '' : `Found it as “${hit.query}” — check the pin, or click the map to move it.`);
      if (results[0]) {
        const L = window.L;
        const lat = parseFloat(results[0].lat), lng = parseFloat(results[0].lon);
        leafletRef.current.setView([lat, lng], 15);
        const icon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          iconSize: [25, 41], iconAnchor: [12, 41],
        });
        if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
        else markerRef.current = L.marker([lat, lng], { icon }).addTo(leafletRef.current);
        setChosen(tidyAddress(results[0].display_name));
      }
    } finally { setSearching(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 680, maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Pick Location</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="Search address…"
            style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13 }}
          />
          <button onClick={doSearch} disabled={searching}
            style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {searching ? '…' : 'Search'}
          </button>
        </div>
        {miss && <div style={{ padding: '8px 16px', fontSize: 12.5, fontWeight: 600, color: miss.startsWith('Found') ? '#92400e' : '#b91c1c', background: miss.startsWith('Found') ? '#fffbeb' : '#fef2f2', borderBottom: '1px solid #e5e7eb' }}>{miss}</div>}
        <div ref={mapRef} style={{ flex: 1, minHeight: 380 }} />
        {chosen && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><path d="M6 1C4.07 1 2.5 2.57 2.5 4.5c0 2.625 3.5 6.5 3.5 6.5s3.5-3.875 3.5-6.5C9.5 2.57 7.93 1 6 1z" stroke="#1d4ed8" strokeWidth="1.2" fill="none"/><circle cx="6" cy="4.5" r="1.2" stroke="#1d4ed8" strokeWidth="1.2" fill="none"/></svg>
            <span style={{ flex: 1 }}>{chosen}</span>
          </div>
        )}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => { const ll = markerRef.current ? markerRef.current.getLatLng() : null; onConfirm(chosen || search, ll ? { lat: ll.lat, lng: ll.lng } : null); onClose(); }} disabled={!chosen && !search}
            style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Use This Location
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY_BID = {
  job_name: '', job_location: '', client_name: '', bid_date: '', bid_time: '',
  pre_bid: 0, pre_bid_mandatory: 0, pre_bid_datetime: null,
  // Two questions, not one: how the number is built, and what kind of work it is.
  // The server derives the legacy bid_type from them.
  price_structure: 'lump_sum', work_type: 'general', job_type: 'Sub-Contractor',
  scope: '', exclusions: '',
  overhead_material_pct: 0, overhead_labor_pct: 0,
  profit_pct: 20, sales_tax_pct: 8.265, supervision_pct: 6, labor_rate: 95,
};

function NewBidModal({ onClose, onCreate, onOldSystem, clients = [], allBids = [] }) {
  const [step, setStep] = useState(0); // 0=choose start, 1=form
  const [copySearch, setCopySearch] = useState('');
  const [form, setForm] = useState({ ...EMPTY_BID });
  const [newStatus, setNewStatus] = useState('draft'); // 'draft' (Upcoming) or 'potential'
  const [pricingType, setPricingType] = useState(null); // 'hard' | 'tm' — chosen after Fresh start
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [clientContacts, setClientContacts] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [extraClients, setExtraClients] = useState([]); // clients added inline during this bid
  const [vendors, setVendors] = useState([]);
  const [extraVendors, setExtraVendors] = useState([]);

  // Same combined, de-duplicated customer + vendor list the bid detail uses, so
  // the New Bid picker matches the Project Owner / Bid Recipient lists.
  useEffect(() => { apiFetch('/vendors').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {}); }, []);
  const [drillRates, setDrillRates] = useState([]);
  // GCs we'll be sending a price to, chosen while creating a SUB bid. Written to
  // bid_recipients right after the bid exists, so the Bid Recipients tab is already
  // populated instead of starting empty.
  const [recipients, setRecipients] = useState([]);
  useEffect(() => { apiFetch('/drill-rates').then(r => setDrillRates(Array.isArray(r) ? r : [])).catch(() => {}); }, []);
  // Pick a drill on a T&M drilling bid → pre-fill its rate (for the current basis) + mobilization.
  function pickDrill(drill) {
    const r = drillRates.find(x => x.drill === drill);
    setForm(f => ({ ...f, tm_drill: drill,
      tm_rate: r ? (f.tm_rate_basis === 'hourly' ? r.hourly_rate : r.daily_rate) : f.tm_rate,
      tm_mobilization: r ? r.mobilization : f.tm_mobilization }));
  }
  function setBasis(basis) {
    const r = drillRates.find(x => x.drill === form.tm_drill);
    setForm(f => ({ ...f, tm_rate_basis: basis, tm_rate: r ? (basis === 'hourly' ? r.hourly_rate : r.daily_rate) : f.tm_rate }));
  }
  // Customers only (Ryan, Oct 1 2026): a bid's owner or recipient is never a vendor, and
  // the vendor list was crowding the type-ahead. Vendors are still loaded for anything
  // else on the form that wants them.
  const companies = buildCompanies([...(clients || []), ...extraClients], []);

  async function startFromCopy(bid) {
    try {
      const newBid = await apiFetch(`/bids/${bid.id}/duplicate`, { method: 'POST' });
      onCreate(newBid);
    } catch (e) { setErr('Copy failed: ' + e.message); }
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function pickCompany(co) {
    setForm(f => ({ ...f, client_id: co.clientId || null, client_name: co.name, client_address1: co.address1 || '', client_address2: co.address2 || '', client_phone: co.phone || '', client_fax: co.fax || '', client_contact: co.contact || '', client_contact_id: null, client_email: co.email || '' }));
    setClientContacts([]);
    if (co.clientId) apiFetch(`/clients/${co.clientId}/contacts`).then(setClientContacts).catch(() => {});
  }
  function clearCompany() {
    setForm(f => ({ ...f, client_id: null, client_name: '', client_address1: '', client_address2: '', client_phone: '', client_fax: '', client_contact: '', client_contact_id: null, client_email: '' }));
    setClientContacts([]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.job_name.trim()) return setErr('Job name is required');
    if (!(form.job_location || '').trim()) return setErr('Location is required — enter an address or use Pick on Map.');
    // On a sub bid the owner is who the work is ultimately for — without it the job
    // can't address a change order or submittal to anyone when it converts.
    if (form.job_type === 'Sub-Contractor' && !(form.client_name || '').trim()) {
      return setErr('Owner is required on a sub-contractor bid — who is the work ultimately for?');
    }
    setSaving(true); setErr('');
    try {
      // price_structure + work_type go up as chosen; the server derives the legacy
      // bid_type from them (a drilling work type → a real drilling bid, so it flows
      // through the existing hole schedule / drill pick / award machinery).
      const payload = { ...form };
      delete payload.bid_type;
      let bid = await apiFetch('/bids', { method: 'POST', body: JSON.stringify(payload) });
      // New bids default to Upcoming; if the user chose Potential, set it right after.
      if (newStatus === 'potential') {
        try { bid = await apiFetch(`/bids/${bid.id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'potential' }) }); } catch {}
      }
      // Save the Hard Bid vs Time & Material choice picked at the start.
      if (pricingType) {
        try { bid = await apiFetch(`/bids/${bid.id}/pricing`, { method: 'PATCH', body: JSON.stringify({ pricing_type: pricingType }) }); } catch {}
      }
      // Save the T&M details — drilling rate (drill/daily-hourly/rate/mob) or the
      // General 'not to exceed' price.
      if (pricingType === 'tm') {
        try { bid = await apiFetch(`/bids/${bid.id}/tm`, { method: 'PATCH', body: JSON.stringify({ drill: form.tm_drill, rate_basis: form.tm_rate_basis || 'daily', rate: form.tm_rate, mobilization: form.tm_mobilization, nte: form.tm_nte }) }); } catch {}
      }
      // Recipients picked during creation — the Bid Recipients tab then opens with
      // the GCs already listed instead of empty. Best-effort: a failure here must not
      // lose the bid that was just created.
      for (const r of recipients) {
        try {
          await apiFetch(`/bids/${bid.id}/recipients`, { method: 'POST', body: JSON.stringify({
            client_id: r.clientId || null, client_name: r.name,
            contact_id: r.contactId || null, contact_name: r.contact || '',
            contact_email: r.email || '', contact_phone: r.phone || '',
          }) });
        } catch { /* keep going — the tab can add whoever didn't take */ }
      }
      onCreate(bid);
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  }

  const field = (label, key, opts = {}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
      {opts.type === 'textarea' ? (
        <textarea value={form[key] || ''} onChange={e => set(key, e.target.value)} rows={3}
          style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
      ) : (
        <input type={opts.type || 'text'} value={form[key] ?? ''} onChange={e => set(key, opts.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
      )}
    </div>
  );

  const filteredBids = allBids.filter(b =>
    (b.job_name || '').toLowerCase().includes(copySearch.toLowerCase()) ||
    (b.client_name || '').toLowerCase().includes(copySearch.toLowerCase())
  ).slice(0, 8);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 600, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>New Bid</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>

        {step === 0 && (
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#374151', fontWeight: 500 }}>Would you like to start from a previous bid?</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => setStep(3)}
                style={{ flex: 1, padding: '14px 12px', borderRadius: 10, border: '2px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 24 }}>📄</span>
                Start Fresh
                <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>Blank new bid</span>
              </button>
              <button type="button" onClick={() => setStep(2)}
                style={{ flex: 1, padding: '14px 12px', borderRadius: 10, border: '2px solid #4338ca', background: '#eef2ff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4338ca', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 24 }}>📋</span>
                Copy a Previous Bid
                <span style={{ fontSize: 11, fontWeight: 400, color: '#6366f1' }}>Start from existing bid</span>
              </button>
              {/* A job quoted in the old system, brought in step by step (Pat, Oct 7 2026). */}
              {onOldSystem && (
                <button type="button" onClick={onOldSystem}
                  style={{ flex: 1, padding: '14px 12px', borderRadius: 10, border: '2px solid #b45309', background: '#fffbeb', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#92400e', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 24 }}>📦</span>
                  New Project From Old System
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#b45309' }}>Files, Fieldwire, old quote → work order</span>
                </button>
              )}
            </div>
            {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
          </div>
        )}

        {step === 3 && (
          <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#374151', fontWeight: 500 }}>How is this job priced?</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => { setPricingType('hard'); setForm(f => ({ ...f, job_type: 'Sub-Contractor' })); setStep(1); }}
                style={{ flex: 1, padding: '18px 12px', borderRadius: 10, border: '2px solid #1e3a5f', background: '#eff6ff', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#1e3a5f', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 26 }}>📐</span>
                Hard Bid
                <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>Fixed / lump sum / unit price</span>
              </button>
              <button type="button" onClick={() => { setPricingType('tm'); setForm(f => ({ ...f, job_type: 'Sub-Contractor' })); setStep(1); }}
                style={{ flex: 1, padding: '18px 12px', borderRadius: 10, border: '2px solid #7c3aed', background: '#f5f3ff', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#7c3aed', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 26 }}>⏱️</span>
                Time &amp; Material
                <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>Billed by hours &amp; materials used</span>
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <button type="button" onClick={() => setStep(0)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>← Back</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Search for a bid to copy — all its details and line items will be duplicated.</p>
            <input autoFocus type="text" placeholder="Search by job name or client…" value={copySearch} onChange={e => setCopySearch(e.target.value)}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 13 }} />
            {filteredBids.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No bids found</div>}
            {filteredBids.map(b => (
              <button key={b.id} type="button" onClick={() => startFromCopy(b)}
                style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#4338ca'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{b.job_name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{b.client_name || '—'}{b.bid_date ? ` · ${b.bid_date}` : ''}</div>
                </div>
                <span style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '3px 8px', borderRadius: 5 }}>{b.status}</span>
              </button>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-start', paddingTop: 4 }}>
              <button type="button" onClick={() => setStep(0)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>← Back</button>
            </div>
            {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
          </div>
        )}

        {step === 1 && (
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {pricingType && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: pricingType === 'tm' ? '#7c3aed' : '#1e3a5f', background: pricingType === 'tm' ? '#f5f3ff' : '#eff6ff', padding: '4px 12px', borderRadius: 999 }}>
                {pricingType === 'tm' ? '⏱️ Time & Material' : '📐 Hard Bid'}
              </span>
              <button type="button" onClick={() => setStep(3)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Change</button>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Bid Status</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['draft', 'Upcoming', '#1e3a5f'], ['potential', 'Potential', '#7c3aed']].map(([val, label, color]) => (
                <button key={val} type="button" onClick={() => setNewStatus(val)}
                  style={{ flex: 1, padding: '9px', borderRadius: 8, border: `1.5px solid ${newStatus === val ? color : '#e5e7eb'}`, background: newStatus === val ? `${color}12` : '#fff', color: newStatus === val ? color : '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>Potential = a lead you're tracking; Upcoming = a bid you're working on.</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field('Job Name *', 'job_name')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Location *</label>
                <button type="button" onClick={() => setShowMap(true)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1C4.07 1 2.5 2.57 2.5 4.5c0 2.625 3.5 6.5 3.5 6.5s3.5-3.875 3.5-6.5C9.5 2.57 7.93 1 6 1z" stroke="currentColor" strokeWidth="1.2" fill="none"/><circle cx="6" cy="4.5" r="1.2" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>
                  Pick on Map
                </button>
              </div>
              <input type="text" value={form.job_location || ''} onChange={e => setForm(f => ({ ...f, job_location: e.target.value, job_lat: null, job_lng: null }))}
                placeholder="Address or click Pick on Map"
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
            </div>
            {field('Bid Date', 'bid_date', { type: 'date' })}
            {field('Bid Time', 'bid_time', { type: 'time' })}
            {/* Mandatory pre-bid meeting — same control as the bid detail page */}
            <PreBidBlock bid={form} isLocked={false}
              updateBid={(k, v) => set(k, v)}
              updateBidFields={obj => setForm(f => ({ ...f, ...obj }))} />
          </div>
          {/* Job Description — the work we're being asked to perform, in plain words.
              This is the bid's Scope of Work; it prints on the quote and can be edited
              on the Scope & Bid Details tile later. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Job Description <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#9ca3af' }}>· what we're being asked to perform</span></label>
            <textarea value={form.scope || ''} onChange={e => set('scope', e.target.value)} rows={3}
              placeholder="e.g. Furnish and install signal poles, pull boxes and conduit for the Stead Blvd intersection…"
              style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>

          {/* Two dropdowns instead of the old mixed list: what kind of work it is, and
              (hard bids only) how the number is built. A T&M bid's structure is its rate
              block below. Whichever work type is picked brings its quote layout and, on
              award, its workflow — the same machinery as before, just asked clearly. */}
          <div style={{ display: 'grid', gridTemplateColumns: pricingType === 'tm' ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Work Type</label>
              <select value={form.work_type || 'general'} onChange={e => setForm(f => ({ ...f, work_type: e.target.value }))}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }}>
                {WORK_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {pricingType !== 'tm' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Price Structure</label>
                <select value={form.price_structure || 'lump_sum'} onChange={e => setForm(f => ({ ...f, price_structure: e.target.value }))}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }}>
                  {PRICE_STRUCTURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
          </div>

          <>
            {/* Prime vs Sub reshapes everything below it, so it gets the same full
                width as Bid Status rather than being squeezed into half a row. Asked on
                T&M bids too — a day rate goes to a GC exactly like a hard number does. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Who are we bidding to?</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['Prime', 'Only one number going out'], ['Sub-Contractor', 'Multiple people we’re submitting prices to']].map(([val, hint]) => {
                  const on = (form.job_type || 'Sub-Contractor') === val;
                  return (
                    <button key={val} type="button" onClick={() => setForm(f => ({ ...f, job_type: val }))}
                      style={{ flex: 1, textAlign: 'left', padding: '11px 14px', borderRadius: 8, cursor: 'pointer',
                        // 1.5px on both, like Bid Status — a thicker selected border
                        // would make the two buttons a pixel different in width.
                        border: `1.5px solid ${on ? '#1d4ed8' : '#e5e7eb'}`, background: on ? '#eff6ff' : '#fff' }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: on ? '#1d4ed8' : '#374151' }}>{val}</div>
                      <div style={{ fontSize: 11, color: on ? '#1e3a5f' : '#9ca3af', marginTop: 3, lineHeight: 1.35 }}>{hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

          </>

          {/* Owner — every bid has one, so it lives outside the Hard/T&M split. On a
              sub bid it sits directly above Bid Recipients, because that's the same
              thought: who the work is for, then who we're quoting it to. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Owner{form.job_type === 'Sub-Contractor' ? ' *' : ''}
              </label>
              {form.client_name && <button type="button" onClick={clearCompany} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Clear</button>}
            </div>
            <CompanyCombo companies={companies} value={form.client_name} placeholder="Type or select a customer, or type a name…"
              allowCreate customersOnly allowOneOff
              onCreated={(kind, rec) => { if (kind === 'client') setExtraClients(prev => [...prev, rec]); else setExtraVendors(prev => [...prev, rec]); }}
              onSelect={pickCompany}
              style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
          </div>
          {form.client_id && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Contact Person</label>
              <ContactManager clientId={form.client_id} contacts={clientContacts} setContacts={setClientContacts}
                selectedId={form.client_contact_id}
                onSelect={cc => {
                  if (cc) setForm(f => ({ ...f, client_contact_id: cc.id, client_contact: cc.name, client_phone: cc.phone || f.client_phone, client_email: cc.email || '' }));
                  else setForm(f => ({ ...f, client_contact_id: null, client_contact: '', client_email: '' }));
                }} />
            </div>
          )}

          {form.client_name && !form.client_id && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', border: '1px dashed #fcd34d', background: '#fffbeb', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>One-time customer — not saved to the directory. Who gets the quote?</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <input value={form.client_contact || ''} onChange={e => set('client_contact', e.target.value)} placeholder="Contact name" style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
                <input value={form.client_email || ''} onChange={e => set('client_email', e.target.value)} placeholder="Email (for the quote)" type="email" style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
                <input value={form.client_phone || ''} onChange={e => set('client_phone', e.target.value)} placeholder="Phone" style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
              </div>
              {/* In the Customer List already? Offer to add them (Pat, Oct 6 2026). */}
              <ContactCheck name={form.client_contact} email={form.client_email} phone={form.client_phone} clientName={form.client_name}
                onUse={c => setForm(f => ({ ...f, client_id: c.client_id, client_name: c.client_name || f.client_name, client_contact_id: c.id, client_contact: c.name, client_email: c.email || f.client_email, client_phone: c.phone || f.client_phone }))}
                onAdded={c => { setForm(f => ({ ...f, client_id: c.client_id, client_contact_id: c.id })); apiFetch(`/clients/${c.client_id}/contacts`).then(setClientContacts).catch(() => {}); }} />
            </div>
          )}

          {/* T&M drilling rate — one drill, daily or hourly, + round-trip mobilization (open-ended) */}
          {pricingType === 'tm' && form.work_type === 'vertical_drilling' && (
            <div style={{ border: '1.5px solid #ede9fe', background: '#faf5ff', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed' }}>⏱️ Time &amp; Material Drilling Rate</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 }}>Drill</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Harold', 'Ella', 'Volvo'].map(d => (
                    <button key={d} type="button" onClick={() => pickDrill(d)}
                      style={{ padding: '9px 16px', borderRadius: 8, border: `1.5px solid ${form.tm_drill === d ? '#92400e' : '#e5e7eb'}`, background: form.tm_drill === d ? '#fffbeb' : '#fff', color: form.tm_drill === d ? '#92400e' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      🛠️ {d}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 }}>Bill by</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[['daily', 'Daily'], ['hourly', 'Hourly']].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setBasis(v)}
                        style={{ padding: '9px 14px', borderRadius: 8, border: `1.5px solid ${(form.tm_rate_basis || 'daily') === v ? '#7c3aed' : '#e5e7eb'}`, background: (form.tm_rate_basis || 'daily') === v ? '#f5f3ff' : '#fff', color: (form.tm_rate_basis || 'daily') === v ? '#7c3aed' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 }}>Rate $/{(form.tm_rate_basis || 'daily') === 'hourly' ? 'hr' : 'day'}</div>
                  <input type="number" inputMode="decimal" value={form.tm_rate ?? ''} onChange={e => set('tm_rate', e.target.value)} placeholder="0"
                    style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 }}>Mobilization $</div>
                  <input type="number" inputMode="decimal" value={form.tm_mobilization ?? ''} onChange={e => set('tm_mobilization', e.target.value)} placeholder="round trip"
                    style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Open-ended — actual days/hours are logged in the field and billed on the job. Rates pre-fill from Drill Rates; edit here for this bid.</div>
            </div>
          )}

          {/* General T&M — a not-to-exceed price for the scope (materials + labor logged in the field) */}
          {pricingType === 'tm' && form.work_type !== 'vertical_drilling' && (
            <div style={{ border: '1.5px solid #ede9fe', background: '#faf5ff', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed' }}>⏱️ Time &amp; Material — {workTypeLabel(form.work_type) || 'General'}</div>
              <div style={{ maxWidth: 220 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 }}>Not-to-Exceed $</div>
                <input type="number" inputMode="decimal" value={form.tm_nte ?? ''} onChange={e => set('tm_nte', e.target.value)} placeholder="e.g. 15000"
                  style={{ border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Billed T&amp;M up to this cap. The field logs materials used + labor hours on the job; enter the scope after creating the bid.</div>
            </div>
          )}
          {/* Bid Recipients — only on a sub bid, because that's the whole difference:
              a prime sends one number to the owner, a sub sends the same number to
              several GCs. Picked here so the Bid Recipients tab opens already filled
              in; it still works exactly as before for adding, sending and pricing. */}
          {/* T&M gets this too. A drilling day rate goes to a GC exactly like a hard
              number does, and leaving it out is why a T&M quote had nobody to address
              and printed the owner instead.
              Note the gate can't ask job_type here: on a T&M bid that field holds the
              Work Flow (General / Vertical Drilling / Loops), not Prime vs Sub. T&M
              never shows the Prime/Sub pillow, so the box simply always appears. */}
          {form.job_type === 'Sub-Contractor' && (
            <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>Bid Recipients</label>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>the contractors you&rsquo;re sending this price to</span>
              </div>
              {recipients.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: i ? '1px solid #eef2f7' : 'none' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', flex: 1 }}>{r.name}</span>
                  {r.clientId ? (r.contact && <span style={{ fontSize: 11, color: '#6b7280' }}>{r.contact}</span>) : (<>
                    <input value={r.contact || ''} placeholder="Contact" onChange={e => setRecipients(rs => rs.map((x, j) => (j === i ? { ...x, contact: e.target.value } : x)))}
                      style={{ width: 110, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 7px', fontSize: 12 }} />
                    <input value={r.email || ''} placeholder="Email for the quote" type="email" onChange={e => setRecipients(rs => rs.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                      style={{ width: 170, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 7px', fontSize: 12 }} />
                  </>)}
                  {!r.clientId && (r.contact || r.email) && (
                    <div style={{ flexBasis: '100%' }}>
                      <ContactCheck name={r.contact} email={r.email} clientName={r.name}
                        onUse={c => setRecipients(rs => rs.map((x, j) => (j === i ? { ...x, clientId: c.client_id, contact: c.name, email: c.email || x.email } : x)))}
                        onAdded={c => setRecipients(rs => rs.map((x, j) => (j === i ? { ...x, clientId: c.client_id } : x)))} />
                    </div>
                  )}
                  <button type="button" onClick={() => setRecipients(rs => rs.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 15, cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>
              ))}
              <div style={{ marginTop: recipients.length ? 8 : 0 }}>
                <CompanyCombo companies={companies} value="" placeholder="Add a contractor, or type a name…"
                  allowCreate customersOnly allowOneOff
                  onCreated={(kind, rec) => { if (kind === 'client') setExtraClients(prev => [...prev, rec]); else setExtraVendors(prev => [...prev, rec]); }}
                  onSelect={co => {
                    if (!co?.name) return;
                    setRecipients(rs => rs.some(x => x.name === co.name) ? rs
                      : [...rs, { clientId: co.clientId || null, name: co.name, contact: co.contact || '', email: co.email || '', phone: co.phone || '' }]);
                  }}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              </div>
              {recipients.length === 0 && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>You can leave this empty and add them on the Bid Recipients tab later.</div>
              )}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: -4 }}>You'll fill in {form.work_type === 'vertical_drilling' || form.work_type === 'horizontal_drilling' ? 'the hole schedule, ' : ''}{pricingType === 'tm' ? 'exclusions' : 'rates, exclusions'} and line items after the bid is created.</div>

          {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button type="button" onClick={() => setStep(0)} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>← Back</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Creating…' : 'Create Bid'}
            </button>
          </div>
        </form>
        )}
      </div>
      {showMap && (
        <LocationPickerModal
          value={form.job_location}
          onConfirm={(loc, ll) => setForm(f => ({ ...f, job_location: loc, job_lat: ll ? ll.lat : null, job_lng: ll ? ll.lng : null }))}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}

function PartsDirectoryModal({ onClose }) {
  const [parts, setParts] = useState([]);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ description: '', unit: 'EACH', material_cost: '', labor_hours: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [uploadingCutSheet, setUploadingCutSheet] = useState({});

  useEffect(() => {
    apiFetch('/parts').then(setParts).catch(() => {});
  }, []);

  const filtered = parts.filter(p => !search.trim() || p.description.toLowerCase().includes(search.toLowerCase()) || (p.category || '').toLowerCase().includes(search.toLowerCase()));
  const categories = [...new Set(parts.map(p => p.category).filter(Boolean))].sort();

  function startAdd() { setEditId('new'); setForm({ description: '', unit: 'EACH', material_cost: '', labor_hours: '', category: '' }); }
  function startEdit(p) { setEditId(p.id); setForm({ description: p.description, unit: p.unit, material_cost: p.material_cost, labor_hours: p.labor_hours, category: p.category || '' }); }
  function cancelEdit() { setEditId(null); }

  async function save() {
    if (!form.description.trim()) return;
    setSaving(true); setSaveErr('');
    try {
      const isNew = editId === 'new';
      const path = isNew ? '/parts' : `/parts/${editId}`;
      const saved = await apiFetch(path, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(form) });
      setParts(ps => isNew ? [...ps, saved] : ps.map(p => p.id === saved.id ? saved : p));
      setEditId(null);
    } catch (e) { setSaveErr(e.message || 'Save failed — make sure the server is running'); }
    finally { setSaving(false); }
  }

  async function deletePart(id) {
    if (!window.confirm('Remove this part from the directory?')) return;
    await apiFetch(`/parts/${id}`, { method: 'DELETE' });
    setParts(ps => ps.filter(p => p.id !== id));
  }

  async function uploadCutSheet(partId, file) {
    setUploadingCutSheet(u => ({ ...u, [partId]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_BASE}/parts/${partId}/cut-sheet`, {
        method: 'POST',
        headers: { 'x-worker-token': getToken() },
        body: fd,
      });
      if (!r.ok) throw new Error('Upload failed');
      const data = await r.json();
      setParts(ps => ps.map(p => p.id === partId ? { ...p, cut_sheet: data.filename } : p));
    } catch (e) { alert(e.message); }
    setUploadingCutSheet(u => ({ ...u, [partId]: false }));
  }

  async function deleteCutSheet(partId) {
    if (!window.confirm('Remove cut sheet for this part?')) return;
    await apiFetch(`/parts/${partId}/cut-sheet`, { method: 'DELETE' });
    setParts(ps => ps.map(p => p.id === partId ? { ...p, cut_sheet: null } : p));
  }

  const cutSheetUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;

  const fmt$ = n => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
  const inputSt = { border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, width: 760, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: '#1e3a5f', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6, flex: 1 }}>📦 Parts Directory</span>
          <button onClick={startAdd} style={{ padding: '5px 14px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add Part</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parts…" style={{ ...inputSt }} autoFocus />
        </div>
        {editId && (
          <div style={{ padding: '14px 20px', background: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#0369a1', marginBottom: 10, textTransform: 'uppercase' }}>{editId === 'new' ? 'New Part' : 'Edit Part'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8 }}>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>DESCRIPTION</div>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Part description" style={inputSt} /></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>UNIT</div>
                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} style={inputSt}>
                  {['EACH','LF','SF','CY','LS','HR','TON','GAL','C','M'].map(u => <option key={u}>{u}</option>)}
                </select></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>MATERIAL COST</div>
                <input type="text" inputMode="decimal" value={form.material_cost} onChange={e => setForm(f => ({ ...f, material_cost: e.target.value }))} placeholder="0.00" style={inputSt} /></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>LABOR HRS</div>
                <input type="text" inputMode="decimal" value={form.labor_hours} onChange={e => setForm(f => ({ ...f, labor_hours: e.target.value }))} placeholder="0.00" style={inputSt} /></div>
              <div><div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>CATEGORY</div>
                <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Conduit" list="part-cats-admin" style={inputSt} />
                <datalist id="part-cats-admin">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
              <button onClick={cancelEdit} style={{ padding: '6px 16px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving || !form.description.trim()}
                style={{ padding: '6px 16px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save Part'}
              </button>
              {saveErr && <span style={{ fontSize: 12, color: '#dc2626' }}>{saveErr}</span>}
            </div>
          </div>
        )}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
              <tr>
                {['Description', 'Unit', 'Mat. Cost', 'Labor Hrs', 'Category', 'Cut Sheet', ''].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Mat. Cost' || h === 'Labor Hrs' ? 'right' : 'left', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: '#111827' }}>{p.description}</td>
                  <td style={{ padding: '9px 12px', color: '#6b7280' }}>{p.unit}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--orange)', fontWeight: 600 }}>{fmt$(p.material_cost)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151' }}>{p.labor_hours}h</td>
                  <td style={{ padding: '9px 12px' }}>{p.category ? <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>{p.category}</span> : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    {p.cut_sheet ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <a href={cutSheetUrl(p.cut_sheet)} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: '#1d4ed8', textDecoration: 'none', fontWeight: 600 }}>📄 View</a>
                        <label style={{ fontSize: 11, color: '#6b7280', cursor: 'pointer' }}>
                          Replace
                          <input type="file" accept="application/pdf" style={{ display: 'none' }}
                            onChange={e => e.target.files[0] && uploadCutSheet(p.id, e.target.files[0])} />
                        </label>
                        <button onClick={() => deleteCutSheet(p.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>✕</button>
                      </div>
                    ) : (
                      <label style={{ fontSize: 11, color: uploadingCutSheet[p.id] ? '#9ca3af' : '#1d4ed8', cursor: 'pointer', fontWeight: 600 }}>
                        {uploadingCutSheet[p.id] ? 'Uploading…' : '+ Upload PDF'}
                        <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={uploadingCutSheet[p.id]}
                          onChange={e => e.target.files[0] && uploadCutSheet(p.id, e.target.files[0])} />
                      </label>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button onClick={() => startEdit(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 12, marginRight: 8 }}>Edit</button>
                    <button onClick={() => deletePart(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12 }}>Delete</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>No parts found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Who can be put on a bid. PMs are who actually estimate — every bid in the system
// belongs to one — plus Executives, and anyone already assigned so an existing
// owner never silently disappears from their own row.
export function estimatorOptionsFor(employees, bids) {
  const ok = u => ['ProjectManager', 'Executive', 'Admin', 'admin'].includes(u.role);
  const assigned = new Set(bids.map(b => String(b.bid_pm_id || '')));
  return employees
    .filter(u => (u.status ? u.status === 'active' : true))
    .filter(u => ok(u) || assigned.has(String(u.id)))
    .filter(u => !/^agent /i.test(`${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim()))
    .sort((a, b) => `${a.firstName || a.first_name || ''} ${a.lastName || a.last_name || ''}`
      .localeCompare(`${b.firstName || b.first_name || ''} ${b.lastName || b.last_name || ''}`));
}

function fmtCityState(loc) {
  if (!loc) return '—';
  const parts = loc.split(',').map(p => p.trim()).filter(Boolean);
  // Short or manually typed — just show as-is
  if (parts.length <= 2) return loc;
  // Nominatim full address ends with "United States" or a country name
  // Format: "house#, street, city, county, state, zip, country"
  const last = parts[parts.length - 1];
  const isNominatim = /United States|Canada|Mexico/i.test(last) && parts.length >= 4;
  if (isNominatim) {
    // Skip country, zip (all digits), county (contains "County")
    const filtered = parts.slice(0, -1).filter(p => !/^\d{5}(-\d{4})?$/.test(p) && !/County/i.test(p));
    // filtered should be: [house#?, street?, city, state]
    if (filtered.length >= 2) return `${filtered[filtered.length - 2]}, ${filtered[filtered.length - 1]}`;
  }
  // Generic: return last two meaningful parts
  return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
}

// The Bids table — the Bids page and the schedule's List both use this one, so the two always
// look and work the same (Ryan, Oct 29 2026). `rows` are the bids to show (unsorted); `setBids`
// is the owner's setter, so a change made here shows everywhere that list is drawn.
export function BidsTable({ rows, setBids, estimators: estimatorOptions }) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('bid_date');
  const [sortDir, setSortDir] = useState('asc');
  const [confirmDelete, setConfirmDelete] = useState(null);

  function handleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  function sortIcon(field) {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  // Reassign straight from the list. Optimistic so the row updates instantly, and
  // it puts the old value back if the save fails rather than showing a lie.
  const [savingPm, setSavingPm] = React.useState(null);
  async function setBidPm(bid, pmId) {
    const previous = bid.bid_pm_id || '';
    if (String(previous) === String(pmId)) return;
    setSavingPm(bid.id);
    setBids(bs => bs.map(b => b.id === bid.id ? { ...b, bid_pm_id: pmId || null } : b));
    try {
      await apiFetch(`/bids/${bid.id}`, { method: 'PATCH', body: JSON.stringify({ bid_pm_id: pmId || null }) });
    } catch (e) {
      setBids(bs => bs.map(b => b.id === bid.id ? { ...b, bid_pm_id: previous || null } : b));
      window.alert('Could not change the estimator: ' + (e?.error || e?.message || 'try again'));
    } finally {
      setSavingPm(null);
    }
  }

  // Change the status straight from the list — same as the estimator: optimistic,
  // reverts if the save fails, and the row-click is stopped so it doesn't open the bid.
  async function setBidStatus(bid, status) {
    const previous = bid.status;
    if (status === previous) return;
    setBids(bs => bs.map(b => b.id === bid.id ? { ...b, status } : b));
    try {
      await apiFetch(`/bids/${bid.id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
    } catch (e) {
      setBids(bs => bs.map(b => b.id === bid.id ? { ...b, status: previous } : b));
      window.alert('Could not change the status: ' + (e?.error || e?.message || 'try again'));
    }
  }

  // Change the bid date from the list.
  async function setBidDate(bid, bid_date) {
    const previous = bid.bid_date || '';
    if ((bid_date || '') === previous) return;
    setBids(bs => bs.map(b => b.id === bid.id ? { ...b, bid_date: bid_date || null } : b));
    try {
      await apiFetch(`/bids/${bid.id}`, { method: 'PATCH', body: JSON.stringify({ bid_date: bid_date || null }) });
    } catch (e) {
      setBids(bs => bs.map(b => b.id === bid.id ? { ...b, bid_date: previous || null } : b));
      window.alert('Could not change the date: ' + (e?.error || e?.message || 'try again'));
    }
  }

  async function deleteBid(id) {
    try {
      await apiFetch(`/bids/${id}`, { method: 'DELETE' });
      setBids(b => b.filter(x => x.id !== id));
    } catch (e) { alert('Delete failed: ' + e.message); }
    setConfirmDelete(null);
  }

  async function duplicateBid(id) {
    try {
      const newBid = await apiFetch(`/bids/${id}/duplicate`, { method: 'POST' });
      setBids(b => [newBid, ...b]);
      navigate(`/admin/bids/${newBid.id}`);
    } catch (e) { alert('Duplicate failed: ' + e.message); }
  }

  const sorted = [...rows].sort((a, b) => {
    let av = a[sortField] || '';
    let bv = b[sortField] || '';
    if (sortField === 'bid_date') {
      av = av || '9999-99-99';
      bv = bv || '9999-99-99';
    }
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
          <div style={{ overflowX: 'auto' }}>
          <table className="admin-table admin-table-compact">
            <thead>
              <tr>
                <th onClick={() => handleSort('job_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Job Name {sortIcon('job_name')}</th>
                <th onClick={() => handleSort('client_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Owner {sortIcon('client_name')}</th>
                <th>Location</th>
                <th onClick={() => handleSort('bid_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>Bid Date {sortIcon('bid_date')}</th>
                <th onClick={() => handleSort('bid_pm_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>Estimator / PM {sortIcon('bid_pm_id')}</th>
                <th>Pre-Bid</th>
                <th>Status</th>
                <th style={{ width: 36 }}></th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(bid => {
                const meta = STATUS_META[bid.status] || STATUS_META.draft;
                const urg = bidUrgency(bid);
                const u = urg && URGENCY[urg];
                if (confirmDelete === bid.id) {
                  return (
                    <tr key={bid.id} style={{ background: '#fef2f2' }}>
                      <td colSpan={8} style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>Delete "{bid.job_name}"? This cannot be undone.</span>
                        <button onClick={() => deleteBid(bid.id)}
                          style={{ marginLeft: 14, padding: '5px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          Yes, Delete
                        </button>
                        <button onClick={() => setConfirmDelete(null)}
                          style={{ marginLeft: 8, padding: '5px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </td>
                      <td />
                    </tr>
                  );
                }
                return (
                  <tr key={bid.id} onClick={() => navigate(`/admin/bids/${bid.id}`)} style={{ cursor: 'pointer', boxShadow: u ? `inset 4px 0 0 ${u.border}` : 'none' }}>
                    <td><span className="admin-list-primary">{bid.job_name}</span></td>
                    <td><span className="admin-list-secondary">{bid.client_name || '—'}</span></td>
                    <td><span className="admin-list-secondary">{fmtCityState(bid.job_location)}</span></td>
                    {/* Change the bid date from the list. A native date input, kept in
                        the urgency colour so an overdue bid still reads red; the row-click
                        is stopped so editing the date doesn't open the bid. */}
                    <td onClick={e => e.stopPropagation()}>
                      {/* No date yet: Safari paints TODAY in an empty date box (Ryan read the
                          undated RTIAA quote as "due 10/5" yet sorted last), so say "Set date"
                          and lay the real, invisible date box over it to open the picker. */}
                      <span style={{ position: 'relative', display: 'inline-block' }}>
                      {!bid.bid_date && <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '3px 5px', display: 'inline-block', border: '1px dashed #d1d5db', borderRadius: 6 }}>Set date</span>}
                      <input type="date"
                        value={bid.bid_date ? String(bid.bid_date).slice(0, 10) : ''}
                        onChange={e => setBidDate(bid, e.target.value)}
                        onClick={e => { e.stopPropagation(); if (typeof e.currentTarget.showPicker === 'function') { try { e.currentTarget.showPicker(); } catch { } } }}
                        title="Bid date — changing it saves right away"
                        style={{
                          border: '1px solid transparent', borderRadius: 6, padding: '3px 5px', fontSize: 12,
                          fontWeight: u ? 700 : 400, color: u ? u.color : 'var(--text)',
                          background: u ? u.bg : 'transparent', cursor: 'pointer', maxWidth: 150, fontFamily: 'inherit',
                          ...(bid.bid_date ? {} : { position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, padding: 0 }),
                        }}
                        onMouseEnter={e => { if (!u && bid.bid_date) { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; } }}
                        onMouseLeave={e => { if (!u && bid.bid_date) { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = 'transparent'; } }} />
                      </span>
                      {(bid.bid_time || urg === 'red') && (
                        <span style={{ fontSize: 11, color: urg === 'red' ? '#b91c1c' : '#9ca3af', marginLeft: 4, whiteSpace: 'nowrap' }}>
                          {bid.bid_time ? `· ${bid.bid_time}` : ''}{urg === 'red' ? ' · Past due' : ''}
                        </span>
                      )}
                    </td>
                    {/* Change the estimator from the list — reassigning shouldn't mean
                        opening the bid, going to Job Description and coming back. The
                        row click opens the bid, so the select has to stop the event. */}
                    <td onClick={e => e.stopPropagation()}>
                      <select
                        value={bid.bid_pm_id || ''}
                        disabled={savingPm === bid.id}
                        onChange={e => setBidPm(bid, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        title="Who's estimating this bid"
                        style={{
                          border: '1px solid transparent', borderRadius: 6, padding: '3px 4px', fontSize: 12,
                          background: savingPm === bid.id ? '#f3f4f6' : 'transparent',
                          color: bid.bid_pm_id ? 'var(--text)' : '#9ca3af',
                          cursor: savingPm === bid.id ? 'default' : 'pointer', maxWidth: 150,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; if (savingPm !== bid.id) e.currentTarget.style.background = 'transparent'; }}>
                        <option value="">— Unassigned —</option>
                        {estimatorOptions.map(u => (
                          <option key={u.id} value={u.id}>{`${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim()}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {bid.pre_bid_mandatory ? (
                        <span title={bid.pre_bid_datetime ? `Mandatory pre-bid: ${fmtPreBid(bid.pre_bid_datetime)}` : 'Mandatory pre-bid meeting'}
                          style={{ fontSize: 10, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', padding: '3px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                          ⚠ Mandatory
                        </span>
                      ) : bid.pre_bid ? (
                        <span title={bid.pre_bid_datetime ? `Pre-bid: ${fmtPreBid(bid.pre_bid_datetime)}` : 'Pre-bid meeting'}
                          style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', padding: '3px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                          Pre-bid
                        </span>
                      ) : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    {/* Change the status from the list. The badge look is kept, but it's
                        a real select now; row-click stopped so it doesn't open the bid. */}
                    <td onClick={e => e.stopPropagation()}>
                      <span className={`admin-list-badge ${meta.color}`} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
                        title="Status — changing it saves right away">
                        {meta.label}
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.6 }}><path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        <select value={bid.status} onChange={e => setBidStatus(bid, e.target.value)} onClick={e => e.stopPropagation()}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none' }}>
                          {Object.keys(STATUS_META).map(key => (
                            <option key={key} value={key}>{STATUS_META[key].label}</option>
                          ))}
                        </select>
                      </span>
                    </td>
                    <td onClick={e => { e.stopPropagation(); duplicateBid(bid.id); }}
                      style={{ textAlign: 'center', padding: '0 8px', cursor: 'pointer', color: '#9ca3af' }}
                      title="Duplicate bid"
                      onMouseEnter={e => e.currentTarget.style.color = '#4338ca'}
                      onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <rect x="4" y="4" width="8" height="9" rx="1"/>
                        <path d="M2 10V2h8"/>
                      </svg>
                    </td>
                    <td onClick={e => { e.stopPropagation(); setConfirmDelete(bid.id); }}
                      style={{ textAlign: 'center', padding: '0 8px', cursor: 'pointer', color: '#9ca3af' }}
                      title="Delete bid"
                      onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                      onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <path d="M1 3h12M5 3V2h4v1M2 3l1 9h8l1-9"/>
                        <path d="M5.5 6v4M8.5 6v4"/>
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
  );
}

export default function AdminBids() {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('upcoming');
  const [showNew, setShowNew] = useState(false);
  const [showOld, setShowOld] = useState(false);   // New Project From Old System (Oct 7 2026)
  const [showRateSheet, setShowRateSheet] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showParts, setShowParts] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Load each list independently so one failed/slow request (e.g. during a
    // server restart) can't wipe out the others. Bids are what the page is
    // really about, so its loading state drives the spinner.
    apiFetch('/bids').then(setBids).catch(e => console.error('bids load failed', e)).finally(() => setLoading(false));
    apiFetch('/clients').then(setClients).catch(e => console.error('clients load failed', e));
    apiFetch('/users').then(setEmployees).catch(e => console.error('users load failed', e));
  }, []);

  function empName(id) {
    if (!id) return '—';
    const e = employees.find(e => String(e.id) === String(id));
    if (!e) return '—';
    return `${e.firstName || e.first_name || ''} ${e.lastName || e.last_name || ''}`.trim() || e.name || '—';
  }

  const estimatorOptions = React.useMemo(() => estimatorOptionsFor(employees, bids), [employees, bids]);

  const activeBids = bids.filter(b => b.status !== 'awarded');
  // Upcoming = bids you still owe (not yet submitted). Once marked Submitted they
  // drop off here and show under the Submitted tab.
  const upcomingBids = bids.filter(b => b.status === 'draft');
  const baseFiltered = statusFilter === 'active' ? activeBids
    : statusFilter === 'all' ? bids
    : statusFilter === 'upcoming' ? upcomingBids
    : bids.filter(b => b.status === statusFilter);

  const q = query.trim().toLowerCase();
  // Search looks across EVERY bid, not just the open tab. Scoped to the tab it was
  // useless in practice: there's no "All" tab, so typing a job you know exists
  // returned nothing whenever it happened to be awarded and you were on Upcoming.
  // The table has a Status column, so results from other tabs still read clearly.
  const searched = q
    ? bids.filter(b => `${b.job_name || ''} ${b.job_location || ''} ${b.client_name || ''} ${b.project_number || ''} ${b.bid_type || ''}`.toLowerCase().includes(q))
    : baseFiltered;

  // Was `new Date(d)` on a bare YYYY-MM-DD, which the spec parses as UTC midnight —
  // so in Reno every bid due date rendered a day EARLY, and disagreed with the
  // urgency pill beside it (bidUrgency above already pins date-only to midday).
  const fmtDate = fmtDateLocal;

  function fmtMoney(n) {
    if (!n && n !== 0) return '—';
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Bids</h1>
          <p className="admin-page-sub">
            {q
              ? <>Searching all {bids.length} bids for “{query.trim()}” · {searched.length} match{searched.length === 1 ? '' : 'es'}</>
              : <>{bids.length} total · {upcomingBids.length} upcoming · {bids.filter(b => b.status === 'submitted').length} submitted</>}
          </p>
          <div style={{ marginTop: 10 }}><ActivityPills pageName="Bids" scope="bids" summaryLabel="Bids Update" /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setShowRateSheet(true)} title="Generate a customer rate sheet PDF"
            style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #7c3aed', background: '#f5f3ff', color: '#7c3aed', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>📄 Rate Sheet</button>
          <button onClick={() => navigate('/admin/price-book')} title="Opens the Price Book — each drill's day, hour and mobilization rates are under Common Items"
            style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #b45309', background: '#fffbeb', color: '#b45309', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>🛠️ Drill Rates</button>
          {/* Bids from the bids mailbox (Ryan, Oct 3 2026). */}
          <button onClick={() => setShowInbox(true)} title="Invitations to bid that were forwarded to the bids mailbox — make a bid from one"
            style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #1e40af', background: '#eff6ff', color: '#1e40af', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>📧 From email</button>
          <button className="admin-primary-btn" onClick={() => setShowNew(true)}>+ New Bid</button>
        </div>
      </div>
      {showRateSheet && <RateSheetModal initial={null} onClose={() => setShowRateSheet(false)} />}
      {showInbox && <BidInboxWindow estimators={estimatorOptions} onClose={() => { setShowInbox(false); }} />}

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 6, padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
          {[['upcoming', 'Upcoming', upcomingBids.length],
            ['active', 'Active', activeBids.length],
            ['potential', 'Potential', bids.filter(b => b.status === 'potential').length],
            ['submitted', 'Submitted', bids.filter(b => b.status === 'submitted').length],
            ['told_low', 'Told Low', bids.filter(b => b.status === 'told_low').length],
            ['lost', 'Lost', bids.filter(b => b.status === 'lost').length],
          ].map(([val, label, count]) => (
            <button key={val} className={`role-tab ${statusFilter === val ? 'active' : ''}`}
              onClick={() => setStatusFilter(val)} style={{ fontSize: 12 }}>
              {label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{count}</span>
            </button>
          ))}
          <button
            className="role-tab"
            onClick={() => navigate('/admin/new-projects')}
            style={{ fontSize: 12, marginLeft: 8, borderLeft: '1px solid #e5e7eb', paddingLeft: 14 }}>
            Awarded → Projects <span style={{ opacity: 0.6, marginLeft: 4 }}>{bids.filter(b => b.status === 'awarded').length}</span>
          </button>
          <div style={{ marginLeft: 'auto', position: 'relative', display: 'flex', alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 13, color: query ? '#e87722' : '#6b7280', pointerEvents: 'none' }}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2.4"/><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/></svg>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search all bids…"
              style={{ padding: '10px 34px 10px 38px', fontSize: 14, fontWeight: 600, border: `2px solid ${query ? '#e87722' : 'var(--border)'}`, borderRadius: 999, background: 'var(--surface)', color: 'var(--text)', width: 290, boxSizing: 'border-box', boxShadow: query ? '0 0 0 3px rgba(232,119,34,0.12)' : '0 1px 2px rgba(0,0,0,0.04)', outline: 'none' }} />
            {query && <button onClick={() => setQuery('')} title="Clear search" style={{ position: 'absolute', right: 9, width: 22, height: 22, borderRadius: 999, border: 'none', background: '#e87722', color: '#fff', fontSize: 14, fontWeight: 700, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : searched.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No bids found</div>
        ) : (
          <BidsTable rows={searched} setBids={setBids} estimators={estimatorOptions} />
        )}
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <button onClick={() => setShowParts(true)}
          style={{ padding: '9px 22px', borderRadius: 8, border: '1.5px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
          📦 Parts Directory
        </button>
      </div>

      {showNew && (
        <NewBidModal
          clients={clients}
          allBids={bids}
          onClose={() => setShowNew(false)}
          onCreate={bid => { setBids(b => [bid, ...b]); setShowNew(false); navigate(`/admin/bids/${bid.id}`); }}
          onOldSystem={() => { setShowNew(false); setShowOld(true); }}
        />
      )}
      {showOld && <OldJobWizard onClose={() => { setShowOld(false); apiFetch('/bids').then(setBids).catch(() => {}); }} onCreated={bid => setBids(b => [bid, ...b])} />}

      {showParts && <PartsDirectoryModal onClose={() => setShowParts(false)} />}
    </AdminLayout>
  );
}
